<lib-base-signal-input
  [label]="label()"
  [hint]="helpText()"
  [showRequired]="showRequiredIndicator()"
  [showError]="displayError()"
  [errorMessage]="displayMessage()"
  [disabled]="disabled()"
  [showExternalLabel]="true"
>
  <div input class="w-full space-y-3 py-2">
    <div
      class="space-y-4"
      [class.hidden]="previewMode() !== 'mobile'"
    >
      @for (row of rows(); track $index; let rowIndex = $index) {
        <section
          class="overflow-hidden rounded-xl border border-gray-200 bg-white"
        >
          <div class="bg-gray-50 px-4 py-3">
            @if (isUserAddedRowIndex(rowIndex)) {
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  class="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  [value]="row.display"
                  [disabled]="disabled() || readonly()"
                  placeholder="Enter item"
                  (input)="updateDisplay(rowIndex, $any($event.target).value)"
                  (blur)="markLocallyTouched()"
                />

                @if (canRemoveRow(rowIndex)) {
                  <button
                    type="button"
                    class="rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                    [disabled]="disabled() || readonly()"
                    aria-label="Remove row"
                    (click)="removeRow(rowIndex)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      class="h-5 w-5"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z"
                      />
                    </svg>
                  </button>
                }
              </div>
            } @else {
              <h3 class="text-sm font-semibold text-gray-900">
                {{ row.display }}
              </h3>
            }
          </div>

          <div class="p-3">
            @switch (effectiveType()) {
              @case ('checkbox') {
                <div
                  class="flex flex-col gap-3"
                  role="group"
                  [attr.aria-label]="row.display || 'Response choices'"
                >
                  @for (option of options(); track option.value) {
                    <label
                      class="flex min-h-[64px] w-full items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
                      [class.cursor-not-allowed]="disabled() || readonly()"
                      [class.border-blue-100]="isSelected(rowIndex, option.value)"
                      [class.bg-blue-50]="isSelected(rowIndex, option.value)"
                      [class.border-gray-200]="!isSelected(rowIndex, option.value)"
                      [class.bg-white]="!isSelected(rowIndex, option.value)"
                    >
                      <lib-signal-checkbox
                        [label]="option.label"
                        [size]="'md'"
                        [value]="isSelected(rowIndex, option.value)"
                        [disabled]="disabled() || readonly()"
                        [id]="'choice-grid-mobile-' + rowIndex + '-' + option.value"
                        (valueChange)="selectOption(rowIndex, option.value, $event)"
                      />
                    </label>
                  }
                </div>
              }
              @case ('button') {
                <div
                  class="grid grid-cols-2 gap-2"
                  role="group"
                  [attr.aria-label]="row.display || 'Response choices'"
                >
                  @for (option of options(); track option.value) {
                    <button
                      type="button"
                      class="min-h-9 min-w-0 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      [class.border-blue-600]="isSelected(rowIndex, option.value)"
                      [class.bg-blue-600]="isSelected(rowIndex, option.value)"
                      [class.text-white]="isSelected(rowIndex, option.value)"
                      [class.border-gray-300]="!isSelected(rowIndex, option.value)"
                      [class.bg-white]="!isSelected(rowIndex, option.value)"
                      [class.text-gray-900]="!isSelected(rowIndex, option.value)"
                      [disabled]="disabled() || readonly()"
                      [attr.aria-pressed]="isSelected(rowIndex, option.value)"
                      (click)="selectOption(rowIndex, option.value)"
                    >
                      {{ option.label }}
                    </button>
                  }
                </div>
              }
              @case ('dropdown') {
                <div class="w-full min-w-0">
                  <lib-signal-multi-select-dropdown
                    [items]="options()"
                    valuePath="value"
                    displayPath="label"
                    [value]="row.selectedChoiceIds"
                    [disabled]="disabled() || readonly()"
                    (valueChange)="updateChoices(rowIndex, $event)"
                  />
                </div>
              }
            }
          </div>
        </section>
      }
    </div>

    <div
      class="w-full overflow-x-auto rounded-lg border border-gray-200"
      [class.hidden]="previewMode() !== 'desktop'"
    >
      <table class="w-full min-w-[420px] table-fixed border-collapse text-sm">
        <thead class="bg-slate-100">
        <tr>
          <th
            class="w-[42%] border-b border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700"
          >
            Item
          </th>

          @if (effectiveType() === 'checkbox') {
            @for (option of options(); track option.value) {
              <th
                class="border-b border-r border-gray-200 px-3 py-3 text-center font-semibold text-gray-700 last:border-r-0"
              >
                {{ option.label }}
              </th>
            }
          }
        </tr>
        </thead>

        <tbody>
          @for (row of rows(); track $index; let rowIndex = $index) {
            <tr class="border-b border-gray-100 last:border-b-0">
              <td class="border-r border-gray-200 px-4 py-3">
                @if (isUserAddedRowIndex(rowIndex)) {
                  <div class="flex items-center gap-2">
                    <input
                      type="text"
                      class="w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      [value]="row.display"
                      [disabled]="disabled() || readonly()"
                      placeholder="Enter item"
                      (input)="updateDisplay(rowIndex, $any($event.target).value)"
                      (blur)="markLocallyTouched()"
                    />

                    @if (canRemoveRow(rowIndex)) {
                      <button
                        type="button"
                        class="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                        [disabled]="disabled() || readonly()"
                        aria-label="Remove row"
                        (click)="removeRow(rowIndex)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          class="h-4 w-4"
                          aria-hidden="true"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z"
                          />
                        </svg>
                      </button>
                    }
                  </div>
                } @else {
                  <span class="font-medium text-gray-900">
                    {{ row.display }}
                  </span>
                }
              </td>

              @switch (effectiveType()) {
                @case ('checkbox') {
                  @for (option of options();
                    track option.value;
                    let last = $last) {
                    <td
                      class="border-r border-gray-200 px-3 py-3 text-center"
                      [class.border-r-0]="last"
                    >
                      <div class="flex items-center justify-center">
                        <lib-signal-checkbox
                          [size]="'md'"
                          [value]="isSelected(rowIndex, option.value)"
                          [disabled]="disabled() || readonly()"
                          [id]="'choice-grid-desktop-' + rowIndex + '-' + option.value"
                          (valueChange)="selectOption(rowIndex, option.value, $event)"
                        />
                      </div>
                    </td>
                  }
                }
                @case ('button') {
                  <td
                    class="border-r border-gray-200 px-3 py-2 last:border-r-0"
                    [attr.colspan]="options().length"
                  >
                    <div
                      class="flex w-full overflow-hidden rounded-lg border border-gray-300"
                      role="group"
                      [attr.aria-label]="row.display || 'Response choices'"
                    >
                      @for (option of options();
                        track option.value;
                        let last = $last) {
                        <button
                          type="button"
                          class="flex-1 px-2 py-1 text-center text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                          [class.border-r]="!last"
                          [class.border-gray-300]="!last"
                          [class.bg-blue-600]="isSelected(rowIndex, option.value)"
                          [class.text-white]="isSelected(rowIndex, option.value)"
                          [class.bg-gray-100]="!isSelected(rowIndex, option.value)"
                          [class.text-gray-900]="!isSelected(rowIndex, option.value)"
                          [disabled]="disabled() || readonly()"
                          [attr.aria-pressed]="isSelected(rowIndex, option.value)"
                          (click)="selectOption(rowIndex, option.value)"
                        >
                          {{ option.label }}
                        </button>
                      }
                    </div>
                  </td>
                }
                @case ('dropdown') {
                  <td
                    class="border-r border-gray-200 px-3 py-2 last:border-r-0"
                    [attr.colspan]="options().length"
                  >
                    <lib-signal-multi-select-dropdown
                      [items]="options()"
                      valuePath="value"
                      displayPath="label"
                      [value]="row.selectedChoiceIds"
                      [disabled]="disabled() || readonly()"
                      (valueChange)="updateChoices(rowIndex, $event)"
                    />
                  </td>
                }
              }
            </tr>
          }
        </tbody>
      </table>
    </div>

    @if (allowAdditionalRows()) {
      <button
        type="button"
        class="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-600 transition hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        [disabled]="disabled() || readonly()"
        (click)="addRow()"
      >
        {{ addRowButtonLabel() }}
      </button>
    }
  </div>
</lib-base-signal-input>
